import { getFundingToken } from '@/utils/api';
import { ttlMsWithStableJitter } from '@/utils/cacheExpiryJitter';

const SESSION_CACHE_KEY = 'ipe:session:v1';
const SESSION_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;
/** session 缓存过期 spread：±1 小时，按 token 指纹稳定分散 */
const SESSION_CACHE_JITTER_SPREAD_MS = 2 * 60 * 60 * 1000;

function tokenFingerprint(token) {
  const t = String(token || '').trim();
  if (!t) return '';
  if (t.length <= 16) return t;
  return `${t.slice(0, 8)}:${t.slice(-8)}`;
}

function effectiveSessionMaxAgeMs(tokenFp) {
  return ttlMsWithStableJitter(
    SESSION_CACHE_MAX_AGE_MS,
    tokenFp || 'ipe-session',
    SESSION_CACHE_JITTER_SPREAD_MS,
  );
}

function parseSessionCacheRaw(raw, storage, removeKey) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed?.user || !parsed?.savedAt) return null;
    const tokenFp = parsed.tokenFp || tokenFingerprint(getFundingToken());
    if (Date.now() - Number(parsed.savedAt) > effectiveSessionMaxAgeMs(tokenFp)) {
      try {
        storage.removeItem(removeKey);
      } catch {
        // ignore
      }
      return null;
    }
    const token = getFundingToken();
    if (!token || tokenFingerprint(token) !== parsed.tokenFp) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/**
 * 跨标签可读：优先 sessionStorage（同标签），再回退 localStorage（新开标签冷启动）。
 * token 指纹不匹配时返回 null，避免误用其它账号的缓存。
 */
export function readIpeSessionCache() {
  if (typeof window === 'undefined') return null;
  try {
    const fromSession = parseSessionCacheRaw(
      sessionStorage.getItem(SESSION_CACHE_KEY),
      sessionStorage,
      SESSION_CACHE_KEY,
    );
    if (fromSession) return fromSession;
  } catch {
    // ignore
  }
  try {
    const fromLocal = parseSessionCacheRaw(
      localStorage.getItem(SESSION_CACHE_KEY),
      localStorage,
      SESSION_CACHE_KEY,
    );
    if (fromLocal) {
      // 新标签命中 localStorage 后灌入本标签 sessionStorage，后续读更稳
      try {
        const raw = localStorage.getItem(SESSION_CACHE_KEY);
        if (raw) sessionStorage.setItem(SESSION_CACHE_KEY, raw);
      } catch {
        // ignore
      }
    }
    return fromLocal;
  } catch {
    return null;
  }
}

export function saveIpeSessionCache(user) {
  if (typeof window === 'undefined' || !user) return;
  const token = getFundingToken();
  if (!token) return;
  const tokenFp = tokenFingerprint(token);
  const payload = JSON.stringify({
    user,
    tokenFp,
    savedAt: Date.now(),
    maxAgeMs: effectiveSessionMaxAgeMs(tokenFp),
  });
  try {
    sessionStorage.setItem(SESSION_CACHE_KEY, payload);
  } catch {
    // sessionStorage 满或隐私模式时忽略
  }
  try {
    localStorage.setItem(SESSION_CACHE_KEY, payload);
  } catch {
    // localStorage 满或隐私模式时忽略
  }
}

export function clearIpeSessionCache() {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(SESSION_CACHE_KEY);
  } catch {
    // ignore
  }
  try {
    localStorage.removeItem(SESSION_CACHE_KEY);
  } catch {
    // ignore
  }
}
